<template>
    <div v-if="json" :class="[`block-values-panels`]" data-aos="custom-fade-in">
        <div class="capper">
            <div class="capper-padded">
                <div :id="swiperContainerId" class="swiper-container">
                    <div class="swiper-wrapper">
                        <div :class="`swiper-slide`"
                             v-for="(item, index) in json"
                             :key="`vp-${index}-${Math.random()}`"
                        >
                            <div :class="[`main-anchor main-anchor-${index % 4}`, (item.valuesImage2?.[0]?.url) ? 'twosy' : '']">
                                <div class="flx">
                                    <div class="flx-l">
                                        <div class="items">
                                            <div class="items2">
                                                <h2 ref="titles" v-html="item.title"></h2>
                                                <div class="caption" ref="captions" v-html="item.caption" />
                                                <div v-if="item.quote" class="quote" ref="quote" v-html="formatQuote(item.quote)" />
                                                <div v-if="item.attribution" class="attribution" ref="attribution" v-html="item.attribution" />
                                            </div>
                                        </div>
                                    </div>
                                    <div class="flx-r">
                                        <div class="bg-panel-outer">
                                            <div class="bg-panel" ref="bgPanel"></div>
                                            <img :alt="item?.valuesImage?.[0]?.title" :src="item?.valuesImage?.[0]?.url" />
                                        </div>
                                        <div v-if="item.valuesImage2?.[0]?.url" class="bg-panel-outer">
                                            <div class="bg-panel" ref="bgPanel"></div>
                                            <img :alt="item?.valuesImage2?.[0]?.title" :src="item?.valuesImage2?.[0].url" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="swiper-nav-elements">
                        <div class="swiper-pagination"></div>
                        <div class="swiper-buttons">
                            <div class="swiper-button swiper-button-prev"></div>
                            <div class="swiper-button swiper-button-next"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import gsap from '@/plugins/gsap';
import SplitText from '@/plugins/gsap/SplitText';
import Swiper, { Navigation, Pagination, Keyboard, EffectFade } from 'swiper';
import 'swiper/swiper-bundle.min.css';
import gh from '@/helpers/gh';
Swiper.use([Navigation, Pagination, Keyboard, EffectFade]);
gsap.registerPlugin(SplitText);

export default {
    name: "ValuesPanels",
    props: {
        json: {
            type: Object,
            required: true,
            default: null,
        },
    },
    data() {
        return {
            gh: gh,
            slider: null,
            swiperContainerId: null,
            swiperContainerSel: null,
            timeoutId: null,
            isDragging: false,
            didOnce: false, // used for subsequent slides (if needed)
        };
    },
    mounted() {
        this.swiperContainerId = 'vpfs-' + Date.now();
        this.swiperContainerSel = '#' + this.swiperContainerId;
        window.addEventListener('scroll', this.onScroll);
        this.$nextTick(() => {
            this.init();
            this.waitForFirstSlide();
        });
    },
    beforeUnmount() {
        clearTimeout(this.timeoutId);
        window.removeEventListener('scroll', this.onScroll);
        if (this.slider) {
            this.slider.destroy();
        }
    },
    methods: {
        init() {
            this.slider = new Swiper(this.swiperContainerSel, {
                init: false,
                effect: 'fade',
                fadeEffect: {
                    crossFade: true
                },
                speed: 750,
                grabCursor: false,
                loop: (this.json.length > 1),
                allowSlidePrev: (this.json.length > 1),
                allowSlideNext: (this.json.length > 1),
                simulateTouch: (this.json.length > 1),
                slidesPerView: 1,
                preventClicks: false,
                preventClicksPropagation: false,
                touchStartPreventDefault: true,
                allowTouchMove: true,
                navigation: {
                    nextEl: this.swiperContainerSel + ' .swiper-button-next',
                    prevEl: this.swiperContainerSel + ' .swiper-button-prev'
                },
                pagination: {
                    el: this.swiperContainerSel + ' .swiper-pagination',
                    type: 'bullets',
                    clickable: true,
                },
                keyboard: {
                    enabled: true,
                    onlyInViewport: true,
                },
            });

            this.slider.on('slideChangeTransitionStart', this.onSlideChange);
            this.slider.on('afterInit', this.afterInit);
            setTimeout(() => this.slider.init(), 100);
        },
        formatQuote(txt) {
            txt = gh.removeTags(txt);
            return '“' + txt + '”';
        },
        afterInit() {
            // Hide text elements for every slide (first slide will be animated via observer)
            this.slider.slides.forEach(slide => {
                let title = slide.querySelector("h2");
                let caption = slide.querySelector(".caption");
                let quote = slide.querySelector(".quote");
                let attribution = slide.querySelector(".attribution");
                let bgPanels = slide.querySelectorAll(".bg-panel");
                let images = slide.querySelectorAll(".bg-panel-outer img");

                if (title) title.style.opacity = 0;
                if (caption) caption.style.opacity = 0;
                if (quote) quote.style.opacity = 0;
                if (attribution) attribution.style.opacity = 0;
                if (images) {
                    images.forEach((el) => {
                        el.style.opacity = 0;
                    })
                }
                if (bgPanels) {
                    bgPanels.forEach((el) => {
                        el.style.opacity = 0;
                    })
                }
            });
            //this.initFirstSlide()
        },
        // For subsequent slides, you can still use onSlideChange/animateSlide as before
        onSlideChange() {
            if (!this.didOnce) return;
            this.$nextTick(() => {
                let activeSlide = this.slider.slides[this.slider.activeIndex];
                if (activeSlide) {
                    this.animateSlide(activeSlide);
                }
            });
        },
        animateSlide(slide) {
            let title = slide.querySelector("h2");
            let caption = slide.querySelector(".caption");
            let quote = slide.querySelector(".quote");
            let attribution = slide.querySelector(".attribution");

            let bgPanels = slide.querySelectorAll(".bg-panel");
            let images = slide.querySelectorAll(".bg-panel-outer img");

            images.forEach((img, index) => {
                gsap.fromTo(img,
                    {opacity: 0, y: 50, x: 50},
                    {opacity: 1, y: 0, x: 0, duration: 0.3, ease: "power2.out", delay: 0.2 + index * 0.2}
                );
            });

            bgPanels.forEach((panel, index) => {
                gsap.fromTo(panel,
                    {opacity: 0, x: -50, y: -50},
                    {opacity: 1, x: 0, y: 0, duration: 0.3, ease: "power2.out", delay: 0.3 + index * 0.2}
                );
            });

            if (title) {
                let splitTitle = new SplitText(title, {type: "words"});
                gsap.set(title, {opacity: 1});
                gsap.fromTo(splitTitle.words, {opacity: 0, y: 15}, {
                    opacity: 1,
                    y: 0,
                    duration: 0.75,
                    delay: 0.3,
                    ease: "power2.out",
                    stagger: 0.1
                });
            }
            if (caption) {
                gsap.fromTo(caption, {opacity: 0, y: 15}, {
                    opacity: 1,
                    y: 0,
                    duration: 0.75,
                    delay: 0.5,
                    ease: "power2.out"
                });
            }
            if (quote) {
                gsap.fromTo(quote, {opacity: 0, y: 15}, {
                    opacity: 1,
                    y: 0,
                    duration: 0.75,
                    delay: 0.6,
                    ease: "power2.out"
                });
            }
            if (attribution) {
                gsap.fromTo(attribution, {opacity: 0, y: 15}, {
                    opacity: 1,
                    y: 0,
                    duration: 0.75,
                    delay: 0.7,
                    ease: "power2.out"
                });
            }
        },
        // --- First Slide Reveal Logic ---
        waitForFirstSlide() {
            // Wait until the first slide is rendered
            const checkSlide = () => {
                const firstSlide = this.slider && this.slider.slides[0];
                if (firstSlide && firstSlide.getBoundingClientRect().height > 0) {
                    // Initialize its elements to hidden
                    //this.initFirstSlide();
                    // Set up an IntersectionObserver at 50% visibility
                    const observer = new IntersectionObserver((entries, obs) => {
                        entries.forEach(entry => {
                            if (entry.intersectionRatio >= 0.5) {
                                this.revealFirstSlide([entry], obs);
                                obs.unobserve(firstSlide);
                            }
                        });
                    }, {threshold: 0.5});
                    observer.observe(firstSlide);
                } else {
                    requestAnimationFrame(checkSlide);
                }
            };
            requestAnimationFrame(checkSlide);
        },
        revealFirstSlide(entries) {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const firstSlide = entry.target;
                    let title = firstSlide.querySelector("h2");
                    let caption = firstSlide.querySelector(".caption");
                    let quote = firstSlide.querySelector(".quote");
                    let attribution = firstSlide.querySelector(".attribution");
                    let bgPanels = firstSlide.querySelectorAll(".bg-panel");
                    let images = firstSlide.querySelectorAll(".bg-panel-outer img");

                    // Animate images (push in)
                    images.forEach((img, index) => {
                        gsap.fromTo(img,
                            {opacity: 0, y: 100, x: 100},
                            {opacity: 1, y: 0, x: 0, duration: 0.3, ease: "power2.out", delay: 0.2 + index * 0.2}
                        );
                    });

                    // Animate background panels (slide out)
                    bgPanels.forEach((panel, index) => {
                        gsap.fromTo(panel,
                            {opacity: 0, x: -100, y: -100},
                            {opacity: 1, x: 0, y: 0, duration: 0.3, ease: "power2.out", delay: 0.3 + index * 0.2}
                        );
                    });

                    // Animate text elements using SplitText
                    if (title) {
                        let splitTitle = new SplitText(title, {type: "words"});
                        gsap.set(title, {opacity: 1});
                        gsap.fromTo(splitTitle.words, {opacity: 0, y: 15}, {
                            opacity: 1,
                            y: 0,
                            duration: 0.75,
                            delay: 0.3,
                            ease: "power2.out",
                            stagger: 0.1
                        });
                    }
                    if (caption) {
                        gsap.fromTo(caption, {opacity: 0, y: 15}, {
                            opacity: 1,
                            y: 0,
                            duration: 0.75,
                            delay: 0.5,
                            ease: "power2.out"
                        });
                    }
                    if (quote) {
                        gsap.fromTo(quote, {opacity: 0, y: 15}, {
                            opacity: 1,
                            y: 0,
                            duration: 0.75,
                            delay: 0.6,
                            ease: "power2.out"
                        });
                    }
                    if (attribution) {
                        gsap.fromTo(attribution, {opacity: 0, y: 15}, {
                            opacity: 1,
                            y: 0,
                            duration: 0.75,
                            delay: 0.7,
                            ease: "power2.out"
                        });
                    }
                }
            });
        },
        // Optional: fallback scroll handler (if needed for subsequent slides)
        onScroll() {
            if (this.didOnce) return;
            let panel = document.querySelector(this.swiperContainerSel);
            if (!panel) return;
            let rect = panel.getBoundingClientRect();
            let viewportHeight = window.innerHeight;
            if (rect.top < viewportHeight * 0.5 && rect.bottom > 0) {
                this.didOnce = true;
                // For the first slide, the IntersectionObserver should handle it,
                // but you can call animateSlide as a fallback:
                this.animateSlide(this.slider.slides[this.slider.activeIndex]);
            }
        }
    },
};
</script>
